import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/upload";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { UsersFour } from "@phosphor-icons/react";
import { fmtDur, toneOf } from "@/context/BreakContext";
const ROLE_LABELS = { admin: "Administrator", sales: "Sales Agent", dispatcher: "Dispatcher" };

const DOT = { active: "bg-emerald-500", on_break: "bg-amber-500", offline: "bg-slate-300" };

function Member({ m, now }) {
  const secs = m.break ? (now - new Date(m.break.started_at).getTime()) / 1000 : 0;
  const over = m.break?.limit_min && secs > m.break.limit_min * 60;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5" data-testid={`team-member-${m.id}`}>
      <span className="relative shrink-0">
        <span className="w-9 h-9 rounded-full bg-slate-100 overflow-hidden grid place-items-center font-bold text-slate-600 text-sm">{m.avatar_url ? <img src={mediaUrl(m.avatar_url)} alt="" className="w-full h-full object-cover" /> : m.name?.[0]?.toUpperCase()}</span>
        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white ${DOT[m.status]}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-slate-900 truncate">{m.name}</span>
        <span className="block text-[11px] text-slate-400">{ROLE_LABELS[m.role] || m.role}</span>
      </span>
      {m.break ? (
        <span className="text-right">
          <span className="block text-[11px] font-bold" style={{ color: toneOf(m.break.type) }}>{m.break.type}</span>
          <span className={`block font-mono text-xs ${over ? "text-red-600 font-bold" : "text-slate-500"}`} data-testid={`team-member-timer-${m.id}`}>{fmtDur(secs)}{over ? " · over" : ""}</span>
        </span>
      ) : <span className={`text-[11px] font-semibold ${m.status === "active" ? "text-emerald-600" : "text-slate-400"}`}>{m.status === "active" ? "Active" : "Offline"}</span>}
    </div>
  );
}

export function TeamStatus() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/team/status").then(({ data }) => alive && setRows(data)).catch(() => {});
    load();
    const id = setInterval(load, open ? 15000 : 45000);
    return () => { alive = false; clearInterval(id); };
  }, [open]);
  useEffect(() => { if (!open) return undefined; const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, [open]);
  const count = (s) => rows.filter((r) => r.status === s).length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-2 h-9 px-3 rounded-full border border-slate-200 text-sm text-slate-600 hover:border-blue-400 transition-colors" data-testid="team-status-btn">
          <UsersFour size={17} weight="duotone" />
          <span className="flex items-center gap-1 font-mono text-xs"><span className="w-2 h-2 rounded-full bg-emerald-500" />{count("active")}<span className="w-2 h-2 rounded-full bg-amber-500 ml-1.5" />{count("on_break")}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 bg-white" data-testid="team-status-panel">
        <div className="px-4 py-3 border-b border-slate-100">
          <p className="font-heading font-bold text-slate-900">Team Status</p>
          <p className="text-xs text-slate-500">{count("active")} active · {count("on_break")} on break · {count("offline")} offline</p>
        </div>
        <div className="max-h-96 overflow-y-auto divide-y divide-slate-50">{rows.map((m) => <Member key={m.id} m={m} now={now} />)}</div>
      </PopoverContent>
    </Popover>
  );
}
